import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Decode a JWT payload without verification — we only need `exp` (seconds
// since epoch) which the backend has already set to the next 02:00
// Europe/Madrid cutoff via `_next_daily_cutoff_utc(iat)`. Returns null on
// any parsing error so callers fall back to the axios 401 interceptor.
const decodeJwtPayload = (jwt) => {
  try {
    const part = jwt.split(".")[1];
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [scopes, setScopes] = useState({});
  const [token, setToken] = useState(localStorage.getItem("wilan_token"));
  const [loading, setLoading] = useState(true);
  // Holds the single active auto-logout timer so token changes / manual
  // logout can cancel it and no stacking timers leak.
  const cutoffTimerRef = useRef(null);

  // Helper function to check permissions
  const hasPermission = useCallback((module, action = "view") => {
    if (!permissions || !permissions[module]) return false;
    return permissions[module].includes(action);
  }, [permissions]);

  // Helper function to check if user can view a module
  const canView = useCallback((module) => hasPermission(module, "view"), [hasPermission]);
  const canCreate = useCallback((module) => hasPermission(module, "create"), [hasPermission]);
  const canEdit = useCallback((module) => hasPermission(module, "edit"), [hasPermission]);
  const canDelete = useCallback((module) => hasPermission(module, "delete"), [hasPermission]);
  // RBAC v2: export permission (new `export` action) and effective data scope.
  const canExport = useCallback((module) => hasPermission(module, "export"), [hasPermission]);
  // Widest scope the user has on a module ('all' | 'team' | 'own'), as merged
  // server-side in /auth/me. Defaults to the narrowest ('own') when unknown.
  const getScope = useCallback((module) => scopes?.[module] || "own", [scopes]);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Visible auto-logout at the daily cutoff — the backend already invalidates
  // the token at 02:00 Europe/Madrid, but an idle tab would otherwise keep
  // showing the UI as if logged in until the next request. We read the
  // token's own `exp` claim (set server-side to `_next_daily_cutoff_utc(iat)`)
  // and schedule a single timer that wipes the session and redirects.
  useEffect(() => {
    // Always clear any previous timer before (potentially) arming a new one.
    if (cutoffTimerRef.current) {
      clearTimeout(cutoffTimerRef.current);
      cutoffTimerRef.current = null;
    }
    if (!token) return;

    const payload = decodeJwtPayload(token);
    const expSec = payload?.exp;
    if (!expSec) return; // Unknown exp → rely on 401 interceptor.

    const msUntilCutoff = expSec * 1000 - Date.now();
    const doLogout = () => {
      localStorage.removeItem("wilan_token");
      delete axios.defaults.headers.common["Authorization"];
      window.location.assign("/login");
    };

    if (msUntilCutoff <= 0) {
      // Token is already past the cutoff (e.g. browser was closed overnight).
      doLogout();
      return;
    }
    cutoffTimerRef.current = setTimeout(doLogout, msUntilCutoff);

    return () => {
      if (cutoffTimerRef.current) {
        clearTimeout(cutoffTimerRef.current);
        cutoffTimerRef.current = null;
      }
    };
  }, [token]);

  // Global 401 interceptor — when the daily-cutoff token expires (or any
  // other auth failure happens mid-session), wipe local credentials and
  // bounce the user to the login page so they re-authenticate. Installed
  // once per AuthProvider mount; cleaned up on unmount to avoid duplicate
  // interceptors during hot-reload.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail || "";
        const onLoginPage = window.location.pathname.startsWith("/login");
        // Only auto-logout on 401s that point to a token problem coming
        // back from our own backend. Other 401s (e.g. wrong credentials on
        // the login form itself) must NOT trigger a redirect because the
        // user is already on the login page and the form needs to surface
        // the error toast itself.
        if (status === 401 && !onLoginPage) {
          const isTokenIssue = /expirad|invalid|token/i.test(detail) || !detail;
          if (isTokenIssue) {
            localStorage.removeItem("wilan_token");
            delete axios.defaults.headers.common["Authorization"];
            // Hard redirect — simpler and more reliable than navigating via
            // react-router from a non-component context.
            window.location.assign("/login");
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Check token validity on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
        setPermissions(response.data.permissions || {});
        setScopes(response.data.scopes || {});
      } catch (error) {
        console.error("Token validation failed:", error);
        localStorage.removeItem("wilan_token");
        setToken(null);
        setUser(null);
        setPermissions({});
        setScopes({});
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      });
      
      const { access_token, user: userData } = response.data;
      localStorage.setItem("wilan_token", access_token);
      setToken(access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      // Fetch full user data with permissions
      const meResponse = await axios.get(`${API_URL}/api/auth/me`);
      setUser(meResponse.data);
      setPermissions(meResponse.data.permissions || {});
      setScopes(meResponse.data.scopes || {});
      
      return { success: true };
    } catch (error) {
      console.error("Login failed:", error);
      return { 
        success: false, 
        error: error.response?.data?.detail || "Error de autenticación" 
      };
    }
  };

  // Login with token (for Microsoft OAuth callback)
  const loginWithToken = async (accessToken) => {
    try {
      // Set the token first
      localStorage.setItem("wilan_token", accessToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
      setToken(accessToken);
      
      // Fetch user data with permissions
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setPermissions(response.data.permissions || {});
      setScopes(response.data.scopes || {});
      
      return { success: true };
    } catch (error) {
      console.error("Token login failed:", error);
      localStorage.removeItem("wilan_token");
      delete axios.defaults.headers.common["Authorization"];
      setToken(null);
      setUser(null);
      setPermissions({});
      setScopes({});
      return { 
        success: false, 
        error: error.response?.data?.detail || "Error al validar token" 
      };
    }
  };

  const logout = () => {
    if (cutoffTimerRef.current) {
      clearTimeout(cutoffTimerRef.current);
      cutoffTimerRef.current = null;
    }
    localStorage.removeItem("wilan_token");
    setToken(null);
    setUser(null);
    setPermissions({});
    setScopes({});
    delete axios.defaults.headers.common["Authorization"];
  };

  const switchRole = async (newRole) => {
    try {
      const response = await axios.put(`${API_URL}/api/auth/switch-role?new_role=${newRole}`);
      const { access_token, active_role } = response.data;
      
      localStorage.setItem("wilan_token", access_token);
      setToken(access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      
      // Fetch updated permissions
      const meResponse = await axios.get(`${API_URL}/api/auth/me`);
      setUser(meResponse.data);
      setPermissions(meResponse.data.permissions || {});
      setScopes(meResponse.data.scopes || {});
      
      return { success: true };
    } catch (error) {
      console.error("Role switch failed:", error);
      return { 
        success: false, 
        error: error.response?.data?.detail || "Error al cambiar rol" 
      };
    }
  };

  const updateNotifications = async (notificationEmail, notificationInapp) => {
    try {
      await axios.put(
        `${API_URL}/api/auth/notifications?notification_email=${notificationEmail}&notification_inapp=${notificationInapp}`
      );
      setUser(prev => ({
        ...prev,
        notification_email: notificationEmail,
        notification_inapp: notificationInapp,
      }));
      return { success: true };
    } catch (error) {
      console.error("Update notifications failed:", error);
      return { 
        success: false, 
        error: error.response?.data?.detail || "Error al actualizar preferencias" 
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    permissions,
    scopes,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canExport,
    getScope,
    login,
    loginWithToken,
    logout,
    switchRole,
    updateNotifications,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
